// RouteRadar — recurring pest control accounts.
// Weekly / monthly / quarterly cadences. Shows who's overdue,
// who's due this week, and who's coming up. One-tap "Mark Serviced"
// after the tech logs the treatment.
import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import { Broadcast, Plus, Trash, CheckCircle, X, Warning, Clock, Calendar, Camera, Sparkle, Path, FilePdf, MapPinLine } from "@phosphor-icons/react";

const STATUS_META = {
  overdue:   { color: "bg-red-500 text-white",       label: "OVERDUE",   icon: <Warning weight="fill" size={12}/> },
  this_week: { color: "bg-amber-400 text-slate-900", label: "THIS WEEK", icon: <Clock weight="fill" size={12}/> },
  soon:      { color: "bg-cyan-500 text-white",      label: "SOON",      icon: <Calendar weight="fill" size={12}/> },
  future:    { color: "bg-slate-400 text-white",     label: "FUTURE",    icon: <Calendar weight="fill" size={12}/> },
};

const initialForm = {
  account_name: "",
  site_address: "",
  cadence: "monthly",
  next_service_date: "",
  default_target_pest: "",
  notes: "",
  contact_name: "",
  contact_phone: "",
};

export default function RouteRadar() {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  // Journal + Optimizer state
  const [journalFor, setJournalFor] = useState(null); // account object
  const [journalData, setJournalData] = useState(null);
  const [routeOpen, setRouteOpen] = useState(false);
  const [routePlan, setRoutePlan] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeStart, setRouteStart] = useState("");

  const load = async () => {
    const r = await api.get("/pest/recurring-accounts");
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!form.account_name || !form.site_address) {
      toast.error("Account name and site address are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/pest/recurring-accounts", {
        ...form,
        next_service_date: form.next_service_date || null,
      });
      toast.success("Account added to route");
      setForm(initialForm);
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this account from the recurring route?")) return;
    await api.delete(`/pest/recurring-accounts/${id}`);
    await load();
  };

  const markServiced = async (id) => {
    try {
      await api.post(`/pest/recurring-accounts/${id}/mark-serviced`);
      toast.success("Serviced — next visit auto-scheduled");
      await load();
    } catch { toast.error("Update failed"); }
  };

  const openJournal = async (acc) => {
    setJournalFor(acc);
    setJournalData(null);
    try {
      const r = await api.get(`/pest/recurring-accounts/${acc.id}/journal`);
      setJournalData(r.data);
    } catch { toast.error("Could not load journal"); }
  };

  const runOptimize = async () => {
    setRouteLoading(true);
    setRoutePlan(null);
    try {
      const r = await api.post("/pest/route-optimize", { start_address: routeStart || null });
      setRoutePlan(r.data);
      if (!r.data.ordered?.length) toast.info(r.data.notes || "Nothing due today");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Route AI failed"));
    } finally { setRouteLoading(false); }
  };

  const photoUrl = (path) => {
    const token = localStorage.getItem("access_token");
    return `${API}/files/${path}?auth=${encodeURIComponent(token || "")}`;
  };

  const downloadJournalPDF = (accId) => {
    const token = localStorage.getItem("access_token");
    window.open(`${API}/pest/recurring-accounts/${accId}/journal.pdf?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  // Auto-Link: "Start Job" stashes the account so PesticideLog auto-links
  // photos there instead of forcing the tech to pick from a dropdown.
  const [activeJob, setActiveJob] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pest_active_job") || "null"); }
    catch { return null; }
  });
  const startJob = (acc) => {
    const payload = { id: acc.id, name: acc.account_name, address: acc.site_address, started_at: Date.now() };
    localStorage.setItem("pest_active_job", JSON.stringify(payload));
    setActiveJob(payload);
    toast.success(`Now on-site at ${acc.account_name} — photos auto-link`);
  };
  const endJob = () => {
    localStorage.removeItem("pest_active_job");
    setActiveJob(null);
    toast.info("Job ended — photos won't auto-link");
  };

  if (!data) return <div className="p-6 text-slate-500">Loading route…</div>;

  return (
    <AdminShell>
    <div className="p-4 md:p-6" data-testid="route-radar-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0f172a] text-white flex items-center justify-center">
            <Broadcast weight="fill" size={22} className="text-emerald-400"/>
          </div>
          <div>
            <h1 className="font-display font-black text-3xl leading-none">Route Radar</h1>
            <div className="text-xs uppercase font-black tracking-widest text-slate-500 mt-1">
              Recurring accounts · quarterly customers never fall through
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setRouteOpen(true); setRoutePlan(null); }}
            data-testid="route-optimize-btn"
            className="h-11 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg">
            <Path weight="fill" size={16}/> Plan My Day
          </button>
          <button onClick={() => setShowForm(v => !v)} data-testid="route-add-btn"
            className="h-11 px-5 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg">
            <Plus weight="bold" size={16}/> {showForm ? "Cancel" : "New Account"}
          </button>
        </div>
      </div>

      {/* Active job banner — proves auto-link is on */}
      {activeJob && (
        <div className="mb-4 p-3 rounded-xl bg-fuchsia-600 text-white flex items-center gap-3 shadow-lg" data-testid="route-active-banner">
          <MapPinLine weight="fill" size={20}/>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Currently On-Site</div>
            <div className="font-bold text-sm truncate">{activeJob.name} · {activeJob.address}</div>
          </div>
          <button onClick={endJob} data-testid="route-active-end"
            className="h-8 px-3 bg-white/20 hover:bg-white/30 text-white font-black uppercase tracking-widest text-[10px] rounded-lg">
            End Job
          </button>
        </div>
      )}

      {/* Counters */}
      <div className="grid grid-cols-4 gap-2 mb-4" data-testid="route-counts">
        {["overdue","this_week","soon","future"].map(k => {
          const m = STATUS_META[k];
          return (
            <div key={k} className={`p-3 rounded-xl ${m.color}`}>
              <div className="text-2xl font-display font-black">{data.counts?.[k] ?? 0}</div>
              <div className="text-[10px] font-black uppercase tracking-widest">{m.label}</div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className="bg-white border-2 border-[#00C4CC] rounded-2xl p-4 md:p-5 mb-5 shadow-lg" data-testid="route-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Account Name *">
              <input value={form.account_name} onChange={e => setF("account_name", e.target.value)}
                placeholder="Joe's Pizza — Downtown" data-testid="route-name" className={inputCls}/>
            </Field>
            <Field label="Site Address *">
              <input value={form.site_address} onChange={e => setF("site_address", e.target.value)}
                placeholder="1200 Sample St" data-testid="route-address" className={inputCls}/>
            </Field>
            <Field label="Cadence">
              <select value={form.cadence} onChange={e => setF("cadence", e.target.value)}
                data-testid="route-cadence" className={inputCls}>
                {(data.cadences || []).map(c => (
                  <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </Field>
            <Field label="Next Service (blank = auto)">
              <input type="date" value={form.next_service_date} onChange={e => setF("next_service_date", e.target.value)}
                data-testid="route-next" className={inputCls}/>
            </Field>
            <Field label="Default Target Pest">
              <input value={form.default_target_pest} onChange={e => setF("default_target_pest", e.target.value)}
                placeholder="German Cockroach" data-testid="route-pest" className={inputCls}/>
            </Field>
            <Field label="Contact Name">
              <input value={form.contact_name} onChange={e => setF("contact_name", e.target.value)}
                placeholder="Joe" data-testid="route-contact" className={inputCls}/>
            </Field>
            <Field label="Contact Phone">
              <input value={form.contact_phone} onChange={e => setF("contact_phone", e.target.value)}
                placeholder="215-555-0100" data-testid="route-phone" className={inputCls}/>
            </Field>
            <label className="block md:col-span-2">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Notes</div>
              <textarea value={form.notes} onChange={e => setF("notes", e.target.value)} rows={2}
                placeholder="Access via back alley. Manager arrives 8 AM."
                data-testid="route-notes"
                className="w-full px-3 py-2 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </label>
          </div>
          <button onClick={save} disabled={saving} data-testid="route-save-btn"
            className="mt-4 h-11 px-6 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 disabled:opacity-50 shadow">
            {saving ? "Saving…" : "Add to Route"}
          </button>
        </div>
      )}

      {/* Accounts list */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {data.accounts.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="route-empty">
            No recurring accounts yet. Tap <b>New Account</b> to add your first regular customer.
          </div>
        ) : data.accounts.map(a => {
          const m = STATUS_META[a.status] || STATUS_META.future;
          return (
            <div key={a.id} data-testid={`route-row-${a.id}`}
              className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0 w-full">
                <div className={`h-9 min-w-[90px] px-2 rounded-lg inline-flex items-center justify-center gap-1 flex-shrink-0 ${m.color}`}>
                  {m.icon}
                  <span className="font-black text-[10px] uppercase tracking-widest">{m.label}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{a.account_name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{a.site_address}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {a.cadence?.toUpperCase()} · Next: <span className="font-mono">{a.next_service_date}</span>
                    {typeof a.days_until === "number" && (
                      <span className={`ml-2 ${a.days_until < 0 ? "text-red-600 font-black" : ""}`}>
                        {a.days_until < 0 ? `${Math.abs(a.days_until)}d overdue` : `in ${a.days_until}d`}
                      </span>
                    )}
                    {a.last_service_date && <span className="ml-2">· Last: {a.last_service_date}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:flex-shrink-0">
                {activeJob?.id === a.id ? (
                  <button onClick={endJob} data-testid={`route-endjob-${a.id}`}
                    className="h-9 px-3 bg-emerald-500 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow">
                    <MapPinLine weight="fill" size={12}/> On-Site · Tap to End
                  </button>
                ) : (
                  <button onClick={() => startJob(a)} data-testid={`route-startjob-${a.id}`}
                    className="h-9 px-3 bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-800 font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                    <MapPinLine weight="fill" size={12}/> Start Job
                  </button>
                )}
                <button onClick={() => openJournal(a)} data-testid={`route-journal-${a.id}`}
                  className="h-9 px-3 bg-slate-800 hover:bg-slate-900 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow">
                  <Camera weight="fill" size={12}/> Journal
                </button>
                <button onClick={() => markServiced(a.id)} data-testid={`route-serviced-${a.id}`}
                  className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow">
                  <CheckCircle weight="fill" size={12}/> Mark Serviced
                </button>
                <button onClick={() => remove(a.id)} data-testid={`route-remove-${a.id}`}
                  className="h-9 w-9 rounded-lg bg-slate-100 hover:bg-red-100 hover:text-red-600 text-slate-400 inline-flex items-center justify-center flex-shrink-0">
                  <Trash size={14}/>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Journal Modal */}
      {journalFor && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4"
          onClick={() => { setJournalFor(null); setJournalData(null); }} data-testid="journal-modal">
          <div onClick={e => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <Camera weight="fill" size={22} className="text-emerald-500"/>
                <div>
                  <div className="font-display font-black text-xl">{journalFor.account_name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{journalFor.site_address}</div>
                </div>
              </div>
              <button onClick={() => { setJournalFor(null); setJournalData(null); }}
                className="text-slate-400 hover:text-slate-800" data-testid="journal-close">
                <X size={20}/>
              </button>
            </div>
            <div className="p-5">
              {!journalData ? (
                <div className="text-center text-slate-400 py-6">Loading journal…</div>
              ) : journalData.entries.length === 0 ? (
                <div className="text-center text-slate-400 py-6" data-testid="journal-empty">
                  No photos yet. Use <b>ID Pest Photo</b> on the Pesticide Log page with this account linked.
                </div>
              ) : (
                <>
                  {Object.keys(journalData.pest_counts || {}).length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {Object.entries(journalData.pest_counts).sort((a,b) => b[1]-a[1]).map(([p, n]) => (
                        <div key={p} className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-[10px] font-black uppercase tracking-widest">
                          {n}× {p}
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={() => downloadJournalPDF(journalFor.id)} data-testid="journal-pdf-btn"
                    className="w-full h-10 mb-4 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center justify-center gap-2 shadow">
                    <FilePdf weight="fill" size={14}/> Download Branded PDF Report
                  </button>
                  <div className="space-y-3">
                    {journalData.entries.map((e, i) => {
                      const id = e.identification || {};
                      return (
                        <div key={i} data-testid={`journal-entry-${i}`}
                          className="flex gap-3 border border-slate-200 rounded-xl p-3">
                          {e.photo_path && (
                            <img src={photoUrl(e.photo_path)} alt="pest"
                              className="w-24 h-24 object-cover rounded-lg bg-slate-100 flex-shrink-0"/>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-sm">{id.pest_specific || id.pest || "Unknown"}</span>
                              {id.confidence && (
                                <span className="text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                                  {id.confidence}
                                </span>
                              )}
                              {id.urgency === "urgent" && (
                                <span className="text-[10px] font-black uppercase tracking-widest bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                                  Urgent
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                              {new Date(e.captured_at).toLocaleString()} · {e.captured_by || ""}
                            </div>
                            {(id.observed || []).length > 0 && (
                              <div className="text-xs text-slate-600 mt-1 line-clamp-2">
                                {id.observed.slice(0, 2).join(" · ")}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Route Optimize Modal */}
      {routeOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !routeLoading && setRouteOpen(false)} data-testid="route-modal">
          <div onClick={e => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <Path weight="fill" size={22} className="text-fuchsia-500"/>
                <div className="font-display font-black text-xl">Plan My Day</div>
              </div>
              <button onClick={() => setRouteOpen(false)} className="text-slate-400 hover:text-slate-800" data-testid="route-close">
                <X size={20}/>
              </button>
            </div>
            <div className="p-5">
              {!routePlan ? (
                <>
                  <div className="text-xs text-slate-500 mb-3">
                    AI will group today's overdue + due accounts by geography and give you a stop order.
                  </div>
                  <label className="block mb-4">
                    <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Where are you starting from?</div>
                    <input list="known-addresses" autoComplete="off" value={routeStart} onChange={e => setRouteStart(e.target.value)}
                      placeholder="Shop address (optional)" data-testid="route-start-input"
                      className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
                  </label>
                  <button onClick={runOptimize} disabled={routeLoading} data-testid="route-optimize-submit"
                    className="w-full h-12 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-50 shadow">
                    <Sparkle weight="fill" size={14}/> {routeLoading ? "Planning…" : "Plan Route"}
                  </button>
                </>
              ) : routePlan.ordered.length === 0 ? (
                <div className="text-center py-6 text-slate-500" data-testid="route-empty-plan">
                  {routePlan.notes || "Nothing due today. Enjoy the day off."}
                </div>
              ) : (
                <div data-testid="route-plan">
                  {routePlan.notes && (
                    <div className="p-3 rounded-lg bg-fuchsia-50 border-l-4 border-fuchsia-500 text-fuchsia-900 text-sm mb-4">
                      {routePlan.notes}
                    </div>
                  )}
                  <div className="space-y-2">
                    {routePlan.ordered.map((s, i) => (
                      <div key={s.id} className="flex gap-3 p-3 border border-slate-200 rounded-xl" data-testid={`route-stop-${i}`}>
                        <div className="h-9 w-9 flex-shrink-0 rounded-lg bg-fuchsia-600 text-white flex items-center justify-center font-display font-black">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm truncate">{s.name}</div>
                          <div className="text-[11px] text-slate-500 truncate">{s.address}</div>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            {s.cluster && (
                              <span className="text-[10px] font-black uppercase tracking-widest bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded">
                                {s.cluster}
                              </span>
                            )}
                            {s.pest && (
                              <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                                {s.pest}
                              </span>
                            )}
                          </div>
                          {s.reason && <div className="text-xs text-slate-600 mt-1">{s.reason}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setRoutePlan(null)} data-testid="route-replan-btn"
                    className="mt-4 w-full h-10 border-2 border-slate-300 hover:bg-slate-100 text-slate-700 font-black uppercase tracking-widest text-xs rounded-xl">
                    Plan Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminShell>
  );
}

const inputCls = "w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
